import { IRestaurant } from '../types/restaurant';

export interface GeoPoint {
  type: 'Point';
  coordinates: [number, number];
}

export interface RawCoordinates {
  longitude?: unknown;
  latitude?: unknown;
}

export const toPoint = ({ longitude, latitude }: RawCoordinates): GeoPoint => {
  const lon = Number(longitude);
  const lat = Number(latitude);

  if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
    throw new Error('Invalid coordinates');
  }

  return {
    type: 'Point',
    coordinates: [lon, lat],
  };
};

export type Ring = Array<[number, number]>;
export type Polygon = Ring[];

export const isInsideRing = ([x, y]: [number, number], ring: Ring): boolean => {
  let inside = false;

  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersects = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
};

export const isInsidePolygon = (point: [number, number], polygon: Polygon): boolean => {
  if (!polygon || polygon.length === 0) {
    return false;
  }

  const [outerRing, ...holes] = polygon;
  if (!isInsideRing(point, outerRing)) {
    return false;
  }

  return holes.every(hole => !isInsideRing(point, hole));
};

const toRad = (value: number): number => (value * Math.PI) / 180;

export const distanceKm = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const lat1Rad = toRad(lat1);
  const lat2Rad = toRad(lat2);
  const a =
    Math.sin(dLat / 2) ** 2 + Math.sin(dLon / 2) ** 2 * Math.cos(lat1Rad) * Math.cos(lat2Rad);
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const computeDeliveryFee = (
  restaurant: Pick<
    IRestaurant,
    'deliveryCostType' | 'deliveryCostRate' | 'deliveryCostMin' | 'location'
  >,
  addressPoint: GeoPoint,
  isPickup: boolean
): number => {
  if (isPickup) {
    return 0;
  }

  const minFee = Number(restaurant.deliveryCostMin ?? 0);
  if ((restaurant.deliveryCostType ?? 'fixed') === 'fixed') {
    return Number(minFee.toFixed(2));
  }

  const coordinates = restaurant.location?.coordinates ?? [];
  const [restLon, restLat] = coordinates.map(coord => Number(coord));
  const [addrLon, addrLat] = addressPoint.coordinates;

  if (![restLon, restLat, addrLon, addrLat].every(Number.isFinite)) {
    throw new Error('Invalid delivery coordinates');
  }

  const rate = Number(restaurant.deliveryCostRate);
  if (!Number.isFinite(rate)) {
    throw new Error('Restaurant delivery cost rate missing');
  }

  const dist = distanceKm(restLat, restLon, addrLat, addrLon);
  return Number(Math.max(rate * dist, minFee).toFixed(2));
};
